import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { complaintAPI, userAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ArrowLeft, Clock, User, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PRIORITY_COLOR = { low: '#2563eb', medium: '#d97706', high: '#ea580c', critical: '#dc2626' };

export default function ComplaintDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isStaffOrAdmin, user } = useAuth();
  const [complaint, setComplaint] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [cd] = await Promise.all([complaintAPI.getById(id)]);
      setComplaint(cd.data.data);
    } catch { toast.error('Ticket not found'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (isStaffOrAdmin) {
      userAPI.getAll().then(({ data }) => setStaff(data.data)).catch(() => {});
    }
  }, [isStaffOrAdmin]);

  const handleAssign = async (staffId) => {
    try {
      await complaintAPI.assign(id, { assignedTo: staffId });
      toast.success('Ticket assigned');
      load();
    } catch { toast.error('Failed to assign'); }
  };

  const handleStatusUpdate = async (status) => {
    setSubmitting(true);
    try {
      await complaintAPI.updateStatus(id, { status, resolutionNotes });
      toast.success(`Ticket ${status}`);
      load();
    } catch { toast.error('Failed to update status'); }
    finally { setSubmitting(false); }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await complaintAPI.addComment(id, { comment });
      setComment('');
      toast.success('Comment added');
      load();
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!complaint) return <div className="empty-state"><h3>Ticket not found</h3></div>;

  const pColor = PRIORITY_COLOR[complaint.priority] || '#2563eb';
  const slaBreach = complaint.slaDeadline && new Date() > new Date(complaint.slaDeadline) && !['resolved', 'closed'].includes(complaint.status);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/complaints')}><ArrowLeft size={14} /></button>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono" style={{ fontWeight: 700, fontSize: '1.1rem' }}>{complaint.ticketNumber}</span>
            <span className={`badge badge-${complaint.status}`} style={{ textTransform: 'capitalize' }}>
              {complaint.status?.replace(/_/g, ' ')}
            </span>
            <span className="badge" style={{ background: `${pColor}20`, color: pColor, textTransform: 'capitalize' }}>
              {complaint.priority}
            </span>
            {slaBreach && <span className="badge badge-danger">SLA Breached</span>}
          </div>
          <p style={{ fontSize: '.9rem', fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>{complaint.title}</p>
        </div>
      </div>

      <div className="grid-2">
        {/* Left: Details + Timeline */}
        <div>
          {/* Customer & Details */}
          <div className="card mb-4">
            <div className="card-header"><h3>Ticket Details</h3></div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: 12 }}>
                <Row label="Customer" value={<Link to={`/customers/${complaint.customer?._id}`} style={{ color: 'var(--primary)', fontWeight: 600 }}>{complaint.customer?.name}</Link>} />
                <Row label="Customer ID" value={<span className="font-mono">{complaint.customer?.customerId}</span>} />
                <Row label="Mobile" value={complaint.customer?.mobile} />
                <Row label="Category" value={complaint.category?.replace(/_/g, ' ')} />
                <Row label="SLA Deadline" value={complaint.slaDeadline ? format(new Date(complaint.slaDeadline), 'dd/MM/yyyy HH:mm') : '—'} />
                <Row label="Created" value={format(new Date(complaint.createdAt), 'dd/MM/yyyy HH:mm')} />
                {complaint.resolvedAt && <Row label="Resolved At" value={format(new Date(complaint.resolvedAt), 'dd/MM/yyyy HH:mm')} />}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="card mb-4">
            <div className="card-header"><h3>Description</h3></div>
            <div className="card-body">
              <p style={{ fontSize: '.875rem', color: 'var(--text)', lineHeight: 1.7 }}>{complaint.description}</p>
            </div>
          </div>

          {/* Resolution */}
          {complaint.resolutionNotes && (
            <div className="card mb-4" style={{ borderColor: 'var(--success)' }}>
              <div className="card-header" style={{ background: '#d1fae5' }}>
                <h3 style={{ color: '#065f46' }}><CheckCircle size={15} style={{ display: 'inline', marginRight: 6 }} />Resolution</h3>
              </div>
              <div className="card-body">
                <p style={{ fontSize: '.875rem' }}>{complaint.resolutionNotes}</p>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="card">
            <div className="card-header"><h3>Activity Timeline</h3></div>
            <div className="card-body">
              <div className="timeline">
                {(complaint.timeline || []).map((t, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-dot">
                      <MessageSquare size={12} color="#fff" />
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-action">{t.action}</div>
                      {t.description && <div className="timeline-desc">{t.description}</div>}
                      <div className="timeline-time">
                        {t.performedBy?.name && <><User size={10} style={{ display: 'inline' }} /> {t.performedBy.name} · </>}
                        {format(new Date(t.timestamp), 'dd/MM/yyyy HH:mm')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add comment */}
              {isStaffOrAdmin && (
                <form onSubmit={handleComment} style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Add Comment</label>
                    <textarea className="form-control" rows={3} value={comment}
                      onChange={e => setComment(e.target.value)} placeholder="Add a note or update..." />
                  </div>
                  <button type="submit" className="btn btn-secondary btn-sm" disabled={submitting || !comment.trim()}>
                    Add Comment
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        {isStaffOrAdmin && (
          <div>
            {/* Assign */}
            <div className="card mb-4">
              <div className="card-header"><h3>Assignment</h3></div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Assign to Staff</label>
                  <select className="form-control" value={complaint.assignedTo?._id || ''}
                    onChange={e => handleAssign(e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {staff.map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}
                  </select>
                </div>
                {complaint.assignedTo && (
                  <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Currently: <strong>{complaint.assignedTo.name}</strong>
                    {complaint.assignedAt && <span> · {format(new Date(complaint.assignedAt), 'dd/MM/yy')}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Status update */}
            {!['resolved', 'closed'].includes(complaint.status) && (
              <div className="card mb-4">
                <div className="card-header"><h3>Update Status</h3></div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">Resolution Notes</label>
                    <textarea className="form-control" rows={3} value={resolutionNotes}
                      onChange={e => setResolutionNotes(e.target.value)}
                      placeholder="Describe what was done to resolve the issue..." />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {complaint.status === 'open' && (
                      <button className="btn btn-warning btn-sm" onClick={() => handleStatusUpdate('in_progress')} disabled={submitting}>
                        Mark In Progress
                      </button>
                    )}
                    {['open', 'in_progress'].includes(complaint.status) && (
                      <button className="btn btn-success btn-sm" onClick={() => handleStatusUpdate('resolved')} disabled={submitting || !resolutionNotes}>
                        <CheckCircle size={13} /> Mark Resolved
                      </button>
                    )}
                    {complaint.status === 'resolved' && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleStatusUpdate('closed')} disabled={submitting}>
                        Close Ticket
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Customer Info Quick View */}
            <div className="card">
              <div className="card-header"><h3>Customer Quick Info</h3></div>
              <div className="card-body">
                <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                  {complaint.customer?.address?.line1}, {complaint.customer?.address?.area}
                </p>
                <Link to={`/customers/${complaint.customer?._id}`} className="btn btn-outline btn-sm w-full" style={{ justifyContent: 'center' }}>
                  View Full Customer Profile
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: '.78rem', color: 'var(--text-muted)', flexShrink: 0, marginRight: 8 }}>{label}</span>
    <span style={{ fontSize: '.85rem', fontWeight: 500, textAlign: 'right', textTransform: 'capitalize' }}>{value || '—'}</span>
  </div>
);
